<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>الدورات</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="showAddDialog = true" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          إضافة دورة
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Main table -->
    <v-data-table
      :headers="headers"
      :items="courses"
      :search="search"
      :loading="loading"
      :items-per-page="10"
      class="elevation-1 rtl-table main-table"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.name }}</td>
          <td>{{ item.desc }}</td>
          <td>{{ formatNumber(item.price_per_person) }} د.ل</td>
          <td>{{ item.capacity }}</td>
          <td>{{ item.available }}</td>
          <td>{{ formatDateRange(item.date_from, item.date_to) }}</td>
          <td>{{ formatTimeRange(item.time_from, item.time_to) }}</td>
          <td>
            <v-chip
              :color="item.active ? 'success' : 'error'"
              text-color="white"
              size="small"
            >
              {{ item.active ? 'نشط' : 'غير نشط' }}
            </v-chip>
          </td>
          <td class="text-center">
            <v-icon small class="mr-2" @click="editItem(item)">
              mdi-pencil
            </v-icon>
            <v-icon small @click="deleteItem(item)">
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showAddDialog" max-width="800px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">
          {{ editedItem.id ? 'تعديل دورة' : 'إضافة دورة جديدة' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.name"
                  label="اسم الدورة"
                  :rules="[v => !!v || 'اسم الدورة مطلوب']"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.desc"
                  label="وصف الدورة"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.price_per_person"
                  label="السعر للشخص الواحد"
                  type="number"
                  :rules="[
                    v => !!v || 'السعر مطلوب',
                    v => v > 0 || 'يجب أن يكون السعر أكبر من صفر'
                  ]"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.capacity"
                  label="السعة"
                  type="number"
                  :rules="[
                    v => !!v || 'السعة مطلوبة',
                    v => v > 0 || 'يجب أن تكون السعة أكبر من صفر',
                    v => Number.isInteger(Number(v)) || 'يجب أن تكون السعة عددًا صحيحًا'
                  ]"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.date_from"
                  label="تاريخ البداية"
                  type="date"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.date_to"
                  label="تاريخ النهاية"
                  type="date"
                  :rules="[
                    v => !editedItem.date_from || !v || new Date(v) >= new Date(editedItem.date_from) || 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية'
                  ]"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.time_from"
                  label="وقت البداية"
                  type="time"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.time_to"
                  label="وقت النهاية"
                  type="time"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-switch
                  v-model="editedItem.active"
                  label="نشط"
                  color="success"
                  hide-details
                  class="rtl-input"
                ></v-switch>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.note"
                  label="ملاحظات"
                  rows="3"
                  class="rtl-input"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showAddDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="blue-darken-1" variant="text" @click="save" :disabled="!valid">
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف هذه الدورة؟
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeleteDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="red-darken-1" variant="text" @click="confirmDelete">
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'CoursesComponent',
  data() {
    return {
      search: '',
      showAddDialog: false,
      showDeleteDialog: false,
      valid: true,
      headers: [
        { title: 'اسم الدورة', key: 'name', align: 'center' },
        { title: 'الوصف', key: 'desc', align: 'center' },
        { title: 'السعر للشخص', key: 'price_per_person', align: 'center' },
        { title: 'السعة', key: 'capacity', align: 'center' },
        { title: 'المتاح', key: 'available', align: 'center' },
        { title: 'التاريخ', key: 'date_range', align: 'center' },
        { title: 'الوقت', key: 'time_range', align: 'center' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      editedItem: {
        id: null,
        name: '',
        desc: '',
        price_per_person: 0,
        capacity: 0,
        date_from: '',
        date_to: '',
        time_from: '',
        time_to: '',
        active: true,
        note: ''
      },
      defaultItem: {
        id: null,
        name: '',
        desc: '',
        price_per_person: 0,
        capacity: 0,
        date_from: '',
        date_to: '',
        time_from: '',
        time_to: '',
        active: true,
        note: ''
      },
      itemToDelete: null
    };
  },
  
  computed: {
    ...mapState({
      courses: state => state.course.courses,
      loading: state => state.course.loading
    })
  },
  
  methods: {
    ...mapActions({
      fetchCourses: 'course/fetchCourses',
      createCourse: 'course/createCourse',
      updateCourse: 'course/updateCourse',
      deleteCourse: 'course/deleteCourse'
    }),
    
    editItem(item) {
      this.editedItem = { ...item };
      this.showAddDialog = true;
    },
    
    deleteItem(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },
    
    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }
      
      try {
        if (this.editedItem.id) {
          // Update existing course
          await this.updateCourse({
            id: this.editedItem.id,
            courseData: this.editedItem
          });
        } else {
          // Create new course
          await this.createCourse(this.editedItem);
        }
        
        this.showAddDialog = false;
        this.resetForm();
      } catch (error) {
        console.error('Error saving course:', error);
      }
    },
    
    async confirmDelete() {
      if (!this.itemToDelete) return;
      
      try {
        await this.deleteCourse(this.itemToDelete.id);
        this.showDeleteDialog = false;
        this.itemToDelete = null;
      } catch (error) {
        console.error('Error deleting course:', error);
      }
    },
    
    resetForm() {
      this.$refs.form?.reset();
      this.editedItem = { ...this.defaultItem };
    },
    
    formatDateRange(dateFrom, dateTo) {
      if (!dateFrom && !dateTo) return 'غير محدد';
      if (!dateFrom) return `حتى ${new Date(dateTo).toLocaleDateString('ar-LY')}`;
      if (!dateTo) return `من ${new Date(dateFrom).toLocaleDateString('ar-LY')}`;
      
      return `${new Date(dateFrom).toLocaleDateString('ar-LY')} - ${new Date(dateTo).toLocaleDateString('ar-LY')}`;
    },
    
    formatTimeRange(timeFrom, timeTo) {
      if (!timeFrom && !timeTo) return 'غير محدد';
      if (!timeFrom) return `حتى ${timeTo}`;
      if (!timeTo) return `من ${timeFrom}`;
      
      return `${timeFrom} - ${timeTo}`;
    },
    
    formatNumber(number) {
      return Number(number).toLocaleString('ar-LY');
    }
  },
  
  watch: {
    showAddDialog(val) {
      if (!val) {
        this.resetForm();
      }
    }
  },
  
  async created() {
    await this.fetchCourses();
    
    // If showAdd query parameter is present, open the add dialog
    if (this.$route.query.showAdd === 'true') {
      this.showAddDialog = true;
    }
  }
};
</script>

<!-- Courses specific styles only -->
<style scoped>
.rtl-table {
  direction: rtl;
  width: 100%;
  margin-bottom: 20px;
}

td.text-center {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.main-table {
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>
